import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Sparkles, X, AlertTriangle, Check } from 'lucide-react';
import { useEditorStore } from '../../store/editorStore';
import { MASK_PRESETS, validateMask } from '../../utils/naming';
import { generateMatrixHall, findMatrixCollisions, rowIndexToLetter } from '../../utils/matrixWizard.logic';
import type { Box2D } from '../../utils/collisionGuard.logic';
import { useShallow } from 'zustand/react/shallow';

interface WizardState {
  rowCount: number;
  racksPerRow: number;
  startTileX: number;
  startTileY: number;
  namingMask: string;
  module: string;
  hall: string;
  rackWidthM: number;
  rackDepthM: number;
  rackHeightM: number;
  aisleWidthM: number;
  rowSpacingM: number;
  trayHeightM: number;
  firstRowOrientation: number;
  alternateOrientation: boolean;
  createAisles: boolean;
  containment: boolean;
  mode: 'replace' | 'append';
}

const DEFAULT_STATE: WizardState = {
  rowCount: 2,
  racksPerRow: 8,
  startTileX: 1,
  startTileY: 1,
  namingMask: '{row_letter}-{rack:02d}',
  module: '1',
  hall: 'A',
  rackWidthM: 0.6,
  rackDepthM: 1.0,
  rackHeightM: 2.0,
  aisleWidthM: 1.2,
  rowSpacingM: 1.2,
  trayHeightM: 0.3,
  firstRowOrientation: 0,
  alternateOrientation: true,
  createAisles: true,
  containment: true,
  mode: 'replace',
};

interface Props {
  onClose: () => void;
}

export const SetupWizard: React.FC<Props> = ({ onClose }) => {
  const { applyMatrixHallBatch, setDcSettings, racks, rows, dcSettings, _paletteSeq } = useEditorStore(useShallow(state => ({
    applyMatrixHallBatch: state.applyMatrixHallBatch,
    setDcSettings: state.setDcSettings,
    racks: state.racks,
    rows: state.rows,
    dcSettings: state.dcSettings,
    _paletteSeq: state._paletteSeq,
  })));

  const [state, setState] = useState<WizardState>(DEFAULT_STATE);
  const [customMaskInput, setCustomMaskInput] = useState('');
  const [usingCustom, setUsingCustom] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [validationError, setValidationError] = useState('');

  const existingRacksList = useMemo(() => Object.values(racks), [racks]);
  const hasExistingRacks = existingRacksList.length > 0;
  const mask = usingCustom ? customMaskInput : state.namingMask;

  // Calculate suggested Y tile for append mode (below existing equipment)
  const suggestedAppendTileY = useMemo(() => {
    let maxBottomPx = 0;
    for (const r of existingRacksList) {
      const bottom = r.y + (r.depthM || 1.0) * 100;
      if (bottom > maxBottomPx) maxBottomPx = bottom;
    }
    return maxBottomPx > 0 ? Math.ceil(maxBottomPx / 60) + 2 : 1;
  }, [existingRacksList]);

  // Continuous row indexing when appending
  const startRowIndex = state.mode === 'append' ? Object.keys(rows).length : 0;

  // Escape key handler
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const update = useCallback(<K extends keyof WizardState>(key: K, value: WizardState[K]) => {
    setState(s => ({ ...s, [key]: value }));
  }, []);

  // Compute live preview structure and exact dimensions
  const previewHall = useMemo(() => {
    return generateMatrixHall({
      rowCount: state.rowCount,
      racksPerRow: state.racksPerRow,
      startTileX: state.startTileX,
      startTileY: state.startTileY,
      startRowIndex,
      rackWidthM: state.rackWidthM,
      rackDepthM: state.rackDepthM,
      rackHeightM: state.rackHeightM,
      aisleWidthM: state.aisleWidthM,
      rowSpacingM: state.rowSpacingM,
      namingMask: mask,
      module: state.module,
      hall: state.hall,
      namingProfiles: dcSettings.namingProfiles,
      namingAssignments: dcSettings.namingAssignments,
      firstRowOrientation: state.firstRowOrientation,
      alternateOrientation: state.alternateOrientation,
      createAisles: state.createAisles,
      containment: state.containment,
      startSeq: _paletteSeq,
    });
  }, [state, startRowIndex, mask, _paletteSeq, dcSettings.namingProfiles, dcSettings.namingAssignments]);

  // Collision detection for append mode
  const appendCollisions = useMemo(() => {
    if (state.mode !== 'append' || !hasExistingRacks) return [];
    const existingBoxes: Box2D[] = existingRacksList.map(r => ({
      id: r.id,
      x: r.x,
      y: r.y,
      w: (r.widthM || 0.6) * 100,
      h: (r.depthM || 1.0) * 100,
      rotation: r.rotation,
    }));
    const existingLabels: Record<string, string> = {};
    existingRacksList.forEach(r => { existingLabels[r.id] = r.label || r.id; });
    return findMatrixCollisions(previewHall.racks, existingBoxes, existingLabels);
  }, [state.mode, hasExistingRacks, existingRacksList, previewHall.racks]);

  const handleGenerate = useCallback(() => {
    const validation = validateMask(mask);
    if (!validation.ok) {
      setValidationError(validation.error || 'Неверная маска именования');
      return;
    }

    if (state.mode === 'replace' && hasExistingRacks && !confirmClear) {
      setConfirmClear(true);
      return;
    }

    // Update settings in store
    setDcSettings({
      namingMask: mask,
      module: state.module,
      hall: state.hall,
      defaultRackWidthM: state.rackWidthM,
      defaultRackDepthM: state.rackDepthM,
      defaultRackHeightM: state.rackHeightM,
      defaultAisleWidthM: state.aisleWidthM,
      defaultRowSpacingM: state.rowSpacingM,
      defaultTrayHeightM: state.trayHeightM,
    });

    const aisleZonesDict: Record<string, any> = {};
    previewHall.aisleZones.forEach(z => {
      aisleZonesDict[z.id] = z;
    });

    // Atomic apply with history record
    applyMatrixHallBatch({
      rows: previewHall.rows,
      racks: previewHall.racks as any[],
      aisleZones: aisleZonesDict,
      replaceExisting: state.mode === 'replace',
    });

    onClose();
  }, [mask, state, hasExistingRacks, confirmClear, setDcSettings, previewHall, applyMatrixHallBatch, onClose]);

  const FIELD: React.CSSProperties = {
    background: 'var(--bg-panel, #111827)',
    border: '1px solid var(--border-color, #374151)',
    borderRadius: 6,
    color: 'var(--text-primary, #f3f4f6)',
    padding: '6px 10px',
    fontSize: 12,
    width: '100%',
    outline: 'none',
    boxSizing: 'border-box',
  };

  const LABEL: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 500,
    color: 'var(--text-secondary, #9ca3af)',
    marginBottom: 4,
    display: 'block',
  };

  const GRID2: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
    marginBottom: 12,
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: 'var(--bg-panel, #111827)', border: '1px solid var(--border-color, #374151)', borderRadius: 12,
        width: 560, maxHeight: '92vh', overflow: 'auto',
        boxShadow: '0 24px 64px rgba(0,0,0,0.8)',
      }}>
        {/* Header */}
        <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid var(--border-color, #374151)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary, #f3f4f6)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Sparkles size={16} style={{ color: '#60a5fa' }} />
              <span>Матричный генератор зала</span>
              <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'rgba(59,130,246,0.15)', color: '#60a5fa', border: '1px solid rgba(59,130,246,0.3)' }}>
                ASHRAE TC 9.9
              </span>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted, #9ca3af)', marginTop: 2 }}>
              Быстрое создание структуры рядов и коридоров в один клик
            </div>
          </div>
          <button onClick={onClose} aria-label="Закрыть" style={{ background: 'none', border: 'none', color: 'var(--text-muted, #9ca3af)', padding: 4, cursor: 'pointer', display: 'flex', alignItems: 'center' }}><X size={18} /></button>
        </div>

        <form onSubmit={e => { e.preventDefault(); handleGenerate(); }} style={{ padding: '16px 20px' }}>
          {/* Mode switch */}
          <div style={{ marginBottom: 14, display: 'flex', gap: 8, background: 'rgba(0,0,0,0.25)', padding: 4, borderRadius: 8 }}>
            <button
              type="button"
              onClick={() => {
                update('mode', 'replace');
                setConfirmClear(false);
                if (state.startTileY === suggestedAppendTileY) {
                  update('startTileY', 1);
                }
              }}
              style={{
                flex: 1, padding: '6px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                background: state.mode === 'replace' ? '#3b82f6' : 'transparent',
                color: state.mode === 'replace' ? '#fff' : '#9ca3af',
              }}
            >
              Чистая замена зала
            </button>
            <button
              type="button"
              onClick={() => {
                update('mode', 'append');
                setConfirmClear(false);
                if (state.startTileY === 1 && suggestedAppendTileY > 1) {
                  update('startTileY', suggestedAppendTileY);
                }
              }}
              style={{
                flex: 1, padding: '6px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                background: state.mode === 'append' ? '#3b82f6' : 'transparent',
                color: state.mode === 'append' ? '#fff' : '#9ca3af',
              }}
            >
              Добавить к плану
            </button>
          </div>

          {/* Collision alert in Append mode */}
          {state.mode === 'append' && appendCollisions.length > 0 && (
            <div style={{
              background: 'rgba(239,68,68,0.12)', border: '1px solid #ef4444',
              borderRadius: 6, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: '#f87171',
              display: 'flex', flexDirection: 'column', gap: 6,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
                <AlertTriangle size={14} />
                <span>Обнаружено наложение ({appendCollisions.length} стоек пересекаются с текущим планом)</span>
              </div>
              <div style={{ fontSize: 11, color: '#fca5a5' }}>
                Например: стойка {appendCollisions[0].candidateLabel} накладывается на {appendCollisions[0].existingLabel}.
              </div>
              <button
                type="button"
                onClick={() => update('startTileY', suggestedAppendTileY)}
                style={{
                  alignSelf: 'flex-start', background: '#374151', border: '1px solid #4b5563',
                  borderRadius: 4, color: '#60a5fa', padding: '4px 8px', fontSize: 11, cursor: 'pointer',
                }}
              >
                Сместить на свободную плитку Y = {suggestedAppendTileY}
              </button>
            </div>
          )}

          {/* Matrix Layout */}
          <div style={{ fontSize: 11, fontWeight: 700, color: '#60a5fa', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
            Матрица рядов и стоек {state.mode === 'append' && `(начиная с ряда ${rowIndexToLetter(startRowIndex)})`}
          </div>
          <div style={GRID2}>
            <div>
              <label style={LABEL} htmlFor="sw-row-count">Количество рядов</label>
              <input id="sw-row-count" autoFocus style={FIELD} type="number" min={1} max={50} value={state.rowCount}
                onChange={e => update('rowCount', Math.max(1, parseInt(e.target.value) || 1))} />
            </div>
            <div>
              <label style={LABEL} htmlFor="sw-racks-per-row">Стоек в каждом ряду</label>
              <input id="sw-racks-per-row" style={FIELD} type="number" min={1} max={200} value={state.racksPerRow}
                onChange={e => update('racksPerRow', Math.max(1, parseInt(e.target.value) || 1))} />
            </div>
          </div>

          {/* Start Tiles */}
          <div style={GRID2}>
            <div>
              <label style={LABEL} htmlFor="sw-tile-x">Стартовая плитка X (столбец 600 мм)</label>
              <input id="sw-tile-x" style={FIELD} type="number" min={0} max={100} value={state.startTileX}
                onChange={e => update('startTileX', Math.max(0, parseInt(e.target.value) || 0))} />
            </div>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <label style={LABEL} htmlFor="sw-tile-y">Стартовая плитка Y (ряд 600 мм)</label>
                {state.mode === 'append' && suggestedAppendTileY > 1 && state.startTileY !== suggestedAppendTileY && (
                  <button
                    type="button"
                    onClick={() => update('startTileY', suggestedAppendTileY)}
                    style={{ background: 'none', border: 'none', color: '#60a5fa', fontSize: 10, cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    подставить {suggestedAppendTileY}
                  </button>
                )}
              </div>
              <input id="sw-tile-y" style={FIELD} type="number" min={0} max={100} value={state.startTileY}
                onChange={e => update('startTileY', Math.max(0, parseInt(e.target.value) || 0))} />
            </div>
          </div>

          {/* Aisles */}
          <div style={GRID2}>
            <div>
              <label style={LABEL} htmlFor="sw-aisle-width">Ширина прохода / Холодный коридор (м)</label>
              <input id="sw-aisle-width" style={FIELD} type="number" min={0.6} max={3} step={0.1} value={state.aisleWidthM}
                onChange={e => update('aisleWidthM', Math.max(0.6, parseFloat(e.target.value) || 1.2))} />
            </div>
            <div>
              <label style={LABEL} htmlFor="sw-row-spacing">Горячий коридор / Спина-к-спине (м)</label>
              <input id="sw-row-spacing" style={FIELD} type="number" min={0} max={3} step={0.1} value={state.rowSpacingM}
                onChange={e => update('rowSpacingM', Math.max(0, parseFloat(e.target.value) || 1.2))} />
            </div>
          </div>

          {/* Rack Defaults */}
          <div style={{ fontSize: 11, fontWeight: 700, color: '#60a5fa', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10, marginTop: 6 }}>
            Параметры шкафа (стандарт 42U)
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 12 }}>
            <div>
              <label style={LABEL} htmlFor="sw-rack-width">Ширина (м)</label>
              <input id="sw-rack-width" style={FIELD} type="number" min={0.4} max={1.2} step={0.05} value={state.rackWidthM}
                onChange={e => update('rackWidthM', Math.max(0.4, parseFloat(e.target.value) || 0.6))} />
            </div>
            <div>
              <label style={LABEL} htmlFor="sw-rack-depth">Глубина (м)</label>
              <input id="sw-rack-depth" style={FIELD} type="number" min={0.6} max={1.5} step={0.05} value={state.rackDepthM}
                onChange={e => update('rackDepthM', Math.max(0.6, parseFloat(e.target.value) || 1.0))} />
            </div>
            <div>
              <label style={LABEL} htmlFor="sw-rack-height">Высота (м)</label>
              <input id="sw-rack-height" style={FIELD} type="number" min={1.0} max={3.0} step={0.1} value={state.rackHeightM}
                onChange={e => update('rackHeightM', Math.max(1.0, parseFloat(e.target.value) || 2.0))} />
            </div>
          </div>

          {/* Orientation & Aisles Options */}
          <div style={GRID2}>
            <div>
              <div style={LABEL}>Ориентация 1-го ряда</div>
              <select style={FIELD} value={state.firstRowOrientation}
                onChange={e => update('firstRowOrientation', parseInt(e.target.value))}>
                <option value={0}>0° (фронт — вниз / Юг)</option>
                <option value={180}>180° (фронт — вверх / Север)</option>
              </select>
            </div>
            <div>
              <label style={LABEL} htmlFor="sw-tray-height">Высота лотка над стойкой (м)</label>
              <input id="sw-tray-height" style={FIELD} type="number" min={0} max={1} step={0.05} value={state.trayHeightM}
                onChange={e => update('trayHeightM', Math.max(0, parseFloat(e.target.value) || 0.3))} />
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-secondary, #e2e8f0)', cursor: 'pointer' }}>
              <input type="checkbox" checked={state.alternateOrientation}
                onChange={e => update('alternateOrientation', e.target.checked)} />
              <span>Чередовать ориентацию фасадов (Front-to-Front / Back-to-Back)</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-secondary, #e2e8f0)', cursor: 'pointer' }}>
              <input type="checkbox" checked={state.createAisles}
                onChange={e => update('createAisles', e.target.checked)} />
              <span>Создавать зоны Холодных и Горячих коридоров</span>
            </label>
            {state.createAisles && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#38bdf8', cursor: 'pointer', marginLeft: 22 }}>
                <input type="checkbox" checked={state.containment}
                  onChange={e => update('containment', e.target.checked)} />
                <span>Изоляция коридоров (Containment — герметичная крыша и двери)</span>
              </label>
            )}
          </div>

          {/* Naming Mask */}
          <div style={{ fontSize: 11, fontWeight: 700, color: '#60a5fa', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
            Шаблон именования
          </div>
          <div style={GRID2}>
            <div>
              <div style={LABEL}>Модуль / Здание</div>
              <input style={FIELD} value={state.module} onChange={e => update('module', e.target.value)} placeholder="1" />
            </div>
            <div>
              <div style={LABEL}>Зал / Секция</div>
              <input style={FIELD} value={state.hall} onChange={e => update('hall', e.target.value)} placeholder="A" />
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
              {MASK_PRESETS.map(p => (
                <button
                  key={p.mask}
                  type="button"
                  onClick={() => { setUsingCustom(false); update('namingMask', p.mask); setValidationError(''); }}
                  style={{
                    fontSize: 10, padding: '3px 8px', borderRadius: 4, cursor: 'pointer',
                    background: !usingCustom && state.namingMask === p.mask ? 'rgba(59,130,246,0.2)' : 'rgba(255,255,255,0.05)',
                    border: !usingCustom && state.namingMask === p.mask ? '1px solid #3b82f6' : '1px solid #374151',
                    color: !usingCustom && state.namingMask === p.mask ? '#60a5fa' : '#9ca3af',
                  }}
                >
                  {p.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => { setUsingCustom(true); setCustomMaskInput(mask); }}
                style={{
                  fontSize: 10, padding: '3px 8px', borderRadius: 4, cursor: 'pointer',
                  background: usingCustom ? 'rgba(251,191,36,0.15)' : 'rgba(255,255,255,0.05)',
                  border: usingCustom ? '1px solid rgba(251,191,36,0.4)' : '1px solid #374151',
                  color: usingCustom ? '#fbbf24' : '#9ca3af',
                }}
              >
                Свой шаблон
              </button>
            </div>

            {usingCustom ? (
              <input style={FIELD} value={customMaskInput} onChange={e => setCustomMaskInput(e.target.value)} placeholder="{module}/{row:02d}/{rack:02d}" />
            ) : (
              <div style={{ fontSize: 11, color: '#9ca3af', fontFamily: 'monospace', padding: '4px 8px', background: 'rgba(0,0,0,0.2)', borderRadius: 4, border: '1px solid #374151' }}>
                {mask}
              </div>
            )}
          </div>

          {/* Preview summary */}
          <div style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid #374151', borderRadius: 6, padding: '10px 14px', marginBottom: 14 }}>
            <div style={{ fontSize: 10, color: '#9ca3af', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Предпросмотр структуры
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {previewHall.racks.slice(0, 6).map(r => (
                <div key={r.id} style={{ fontSize: 12, fontFamily: 'monospace', color: '#60a5fa' }}>
                  {r.label}
                </div>
              ))}
              {previewHall.racks.length > 6 && (
                <div style={{ fontSize: 11, color: '#6b7280' }}>... (+{previewHall.racks.length - 6} ст.)</div>
              )}
            </div>
            <div style={{ fontSize: 11, color: '#e2e8f0', marginTop: 8, display: 'flex', justifyContent: 'space-between' }}>
              <span>Рядов: <b>{state.rowCount}</b> × <b>{state.racksPerRow}</b> ст. = <b>{previewHall.totalRacks}</b> стоек (42U)</span>
              <span>Габариты: <b>{previewHall.totalWidthM}</b> × <b>{previewHall.totalDepthM}</b> м</span>
            </div>
          </div>

          {/* Validation error */}
          {validationError && (
            <div style={{
              background: 'rgba(239,68,68,0.1)', border: '1px solid #ef4444',
              borderRadius: 6, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: '#ef4444',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <AlertTriangle size={14} />
              <span>{validationError}</span>
            </div>
          )}

          {/* Confirm clear warning */}
          {confirmClear && (
            <div style={{
              background: 'rgba(239,68,68,0.1)', border: '1px solid #ef4444',
              borderRadius: 6, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: '#ef4444',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <AlertTriangle size={14} />
              <span>Будет удалено {Object.keys(racks).length} существующих стоек. Нажмите кнопку ещё раз для подтверждения.</span>
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={{
              padding: '8px 16px', borderRadius: 6, border: '1px solid #374151',
              background: 'transparent', color: '#9ca3af', fontSize: 12, cursor: 'pointer',
            }}>
              Отмена
            </button>
            <button type="submit" style={{
              padding: '8px 20px', borderRadius: 6, border: 'none',
              background: confirmClear ? '#ef4444' : (appendCollisions.length > 0 ? '#d97706' : '#2563eb'),
              color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}>
              {confirmClear ? (
                <>
                  <AlertTriangle size={14} /> Да, заменить
                </>
              ) : appendCollisions.length > 0 ? (
                <>
                  <Check size={14} /> Построить, несмотря на коллизии ({previewHall.totalRacks} ст.)
                </>
              ) : (
                <>
                  <Check size={14} /> Построить зал ({previewHall.totalRacks} стоек)
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
